import RollupTreeGroupActionsTable from './datasets/rollup-tree-group-actions-table'
import $ from 'jquery'
import icon from 'scoreboard/components/icon'

/**
 * Renders the Actions column in the Organization Permissions panel
 */
import _ from 'lodash'

import { permissionIdsOrderMap, permissionLabels } from '../components/permissions-metadata'
import i18n from '../components/i18n'

/**
 * Renders the names of the given permissions in the order they appear on
 * Admin > Groups
 */
function renderGroupActions(permissionIds) {
	let sortedPermissionIds = _.sortBy(permissionIds, permissionId =>
		permissionIdsOrderMap[permissionId])
	return _(sortedPermissionIds)
		.map(permissionId => {
			let key = permissionLabels()[permissionId]
			return _.escape(i18n.get(key))
		})
		.join(', ')
}

/**
 * Renders a comma-separated list of users
 * @param {Object<Number, String>} userLabelsById user labels by ID
 * @param userIds the IDs of the users to render
 * @return {string} the HTML to display to a user for the given users
 */
function renderUsers(userLabelsById, userIds) {
	return _(userIds)
		.map(id => ({id, label: userLabelsById[id]}))
		.sortBy(user => user.label)
		.map(renderUser)
		.join('') // Prevent adding a comma between users
}

/**
 * Renders a link and icon for a specific user.
 * Required params are defined in renderUserLink().
 */
function renderUser(params = {}) {
	let linkHtml = renderUserLink(params)
	let iconName = 'user-avatar'
	return labelWithIcon(linkHtml, iconName, 'avatar')
}

/**
 *
 * @param id The unique ID of the user
 * @param {string} label The username of the user
 * @returns The link html for the user's admin/users page
 */
function renderUserLink({ id, label } = {}) {
	return `<a class="sp-navigate text-link" href="admin/users/${id}">` +
				_.escape(label) +
			'</a>'
}

function renderGroup({ id, label } = {}) {
	let linkHtml =
		`<a class="sp-navigate text-link" href="admin/groups/${id}">` +
		_.escape(label) +
		'</a>'
	let iconName = 'profile'
	return labelWithIcon(linkHtml, iconName, 'avatar')
}

function labelWithIcon(linkHtml, iconName, iconClass = '') {
	return '<div class="label-with-icon">' +
		`<span class="icon-container ${iconClass}">${icon(iconName)}</span>` +
		`<div>${linkHtml}</div>` +
		'</div>'
}


/**
 * Renders html that includes a link and icon for a rollup tree group.
 * @param {Object} params - The parameters required for
 * renderRollupTreeGroupLink(). Includes rollupTreeGroupId, rollupTreeGroupLabel,
 * treeRootId, fieldLabel, and organizationId.
 * Descriptions for each param are defined in renderRollupTreeGroupLink().
 * return {string} the HTML
 */
function renderRollupTreeGroup(params = {}) {
	let linkHtml  = renderRollupTreeGroupLink(params)
	let iconName = 'rollup-tree'
	return labelWithIcon(linkHtml, iconName)
}

/**
 * Renders a link to a rollup tree group. Clicking on the link is handled
 * by the onClickRollupTreeGroup event handler.
 * @param rollupTreeGroupId the ID of the rollup tree group
 * @param rollupTreeGroupLabel the name of the rollup tree group
 * @param treeRootId the ID of the dataset tree the
 * @param fieldLabel the name of the field associated with the rollup tree
 * group
 * @param organizationId the ID of the organization
 * return {string} the HTML
 */
function renderRollupTreeGroupLink({
	rollupTreeGroupId, rollupTreeGroupLabel,
	treeRootId, fieldLabel, organizationId
} = {}) {
	let label = _.escape(rollupTreeGroupLabel)
	if (fieldLabel) {
		label = _.escape(fieldLabel) + ': ' + label
	}
	return '<a class="text-link template"' +
		` data-organization-id="${organizationId}"` +
		` data-rollup-tree-group-id="${rollupTreeGroupId}"` +
		` data-rollup-tree-group-label="${_.escape(rollupTreeGroupLabel)}"` +
		` data-tree-root-id="${treeRootId}">${label}</a>`
}

/**
 * Callback handler to execute when a rollup tree group link created by
 * renderRollupTreeGroup is clicked
 * @param e the event
 */
function onClickRollupTreeGroup(e) {
	RollupTreeGroupActionsTable.dialog({
		organizationId: $(e.currentTarget).data('organization-id'),
		rollupTreeGroupId: $(e.currentTarget).data('rollup-tree-group-id'),
		rollupTreeGroupLabel: $(e.currentTarget).data('rollup-tree-group-label'),
		treeRootId: $(e.currentTarget).data('tree-root-id'),
		banner: true,
		errorTooltipTarget: $(e.currentTarget)
	})
}

/** @param dp the dataset permission */
function renderDatasetActions(dp) {
	let permissions = []
	if (dp.administer) {
		permissions.push('datasets.permissions.administer')
	}
	if (dp.modifyPermissions) {
		permissions.push('datasets.permissions.modifyPermissions')
	}
	if (dp.modifyRecords) {
		permissions.push('datasets.permissions.modifyRecords')
	}
	if (dp.viewRecords) {
		permissions.push('datasets.permissions.viewRecords')
	}
	if (dp.viewRecordsTab) {
		permissions.push('datasets.permissions.viewRecordsTab')
	}
	if (dp.modifyViews) {
		permissions.push('datasets.permissions.modifyViews')
	}
	return _(permissions)
		.map(permission => i18n.get(permission))
		.join(', ')
}

/**
 * Returns whether we should display the "Send To" button
 * @param {Number|String} id the ID of the item being audited (user, org, or
 * dataset)
 * @param {Boolean} exportEnabled whether exports are currently enabled
 */
function isDisplayingSendTo(id, exportEnabled) {
	return !!(id && exportEnabled)
}

export { renderGroupActions, renderUser, renderGroup, renderDatasetActions,
	renderRollupTreeGroup, onClickRollupTreeGroup, renderUsers,
	isDisplayingSendTo, renderRollupTreeGroupLink, labelWithIcon,
	renderUserLink }
